/**
 * Seasonality Calculation Utilities
 *
 * Shared functions for calculating weighted seasonality distributions.
 * Used by both SeasonalityModeView (for display) and Projections page (for saving).
 *
 * This ensures the frontend calculation matches what gets stored in the database,
 * eliminating discrepancies between the Projections page and Forecast page purple line.
 */

import { MonthlyTotals, createEmptyMonthlyTotals } from '../types';

// Minimum number of months with data required for a year to be included in seasonality calculation
// This prevents partial years (e.g., only Nov-Dec for a newly acquired location) from skewing the weights
export const MIN_MONTHS_FOR_SEASONALITY = 6;

/**
 * Sum all months in a MonthlyTotals object
 */
export function sumMonths(m: MonthlyTotals): number {
  return m.month1 + m.month2 + m.month3 + m.month4 +
         m.month5 + m.month6 + m.month7 + m.month8 +
         m.month9 + m.month10 + m.month11 + m.month12;
}

/**
 * Count how many months have non-zero data
 */
export function countMonthsWithData(m: MonthlyTotals): number {
  let count = 0;
  if (m.month1 !== 0) count++;
  if (m.month2 !== 0) count++;
  if (m.month3 !== 0) count++;
  if (m.month4 !== 0) count++;
  if (m.month5 !== 0) count++;
  if (m.month6 !== 0) count++;
  if (m.month7 !== 0) count++;
  if (m.month8 !== 0) count++;
  if (m.month9 !== 0) count++;
  if (m.month10 !== 0) count++;
  if (m.month11 !== 0) count++;
  if (m.month12 !== 0) count++;
  return count;
}

/**
 * Check if the year total is mathematically stable for percentage calculations.
 * This prevents extreme percentages when large positive/negative values nearly cancel out.
 * Example: YTD = -$27 with individual months of -$4,050 would give 15,000%+ percentages
 */
export function hasStableSeasonalityData(m: MonthlyTotals): boolean {
  const total = sumMonths(m);
  const absTotal = Math.abs(total);

  // If total is zero, it's not stable (handled elsewhere)
  if (absTotal === 0) return false;

  // Find the max absolute value of any individual month
  const monthValues = [
    m.month1, m.month2, m.month3, m.month4,
    m.month5, m.month6, m.month7, m.month8,
    m.month9, m.month10, m.month11, m.month12,
  ];
  const maxAbsMonth = Math.max(...monthValues.map(v => Math.abs(v)));

  // If any month's absolute value exceeds 2x the absolute total,
  // the data is unstable for percentage calculations
  // (This means the percentage for that month would exceed 200%)
  return maxAbsMonth <= absTotal * 2;
}

/**
 * Calculate monthly percentages from a MonthlyTotals object
 * Returns array of 12 percentages (0-1)
 */
export function getMonthlyPercentages(m: MonthlyTotals): number[] {
  const total = sumMonths(m);
  if (total === 0) return Array(12).fill(1 / 12);
  return [
    m.month1 / total, m.month2 / total, m.month3 / total, m.month4 / total,
    m.month5 / total, m.month6 / total, m.month7 / total, m.month8 / total,
    m.month9 / total, m.month10 / total, m.month11 / total, m.month12 / total,
  ];
}

/**
 * Check if a year has sufficient data for seasonality calculation
 * Requires at least MIN_MONTHS_FOR_SEASONALITY months of data and stable totals
 */
export function hasEnoughDataForSeasonality(m: MonthlyTotals | undefined): boolean {
  if (!m) return false;
  if (sumMonths(m) === 0) return false;
  // Check for mathematical stability - prevents extreme percentages when
  // large positive/negative values nearly cancel out (e.g., YTD = -$27 with months of -$4,050)
  if (!hasStableSeasonalityData(m)) return false;
  return countMonthsWithData(m) >= MIN_MONTHS_FOR_SEASONALITY;
}

/**
 * Historical year data for seasonality calculation
 */
export interface HistoricalYearData {
  year: number;
  monthlyTotals: MonthlyTotals;
}

/**
 * Result of seasonality calculation
 */
export interface SeasonalityResult {
  monthlyDistribution: MonthlyTotals;
  seasonalityWeights: MonthlyTotals;
  yearsUsed: number[];
}

/**
 * Calculate weighted seasonality percentages from historical years
 *
 * Weights: Most recent year 50%, previous 30%, oldest 20%
 * Years with < 6 months of data are excluded
 * Weights are normalized based on which years are actually used
 *
 * @param historicalYears - Array of up to 3 years of historical data (most recent first)
 * @returns Array of 12 percentages (0-1) representing monthly distribution
 */
export function calculateSeasonalityWeights(
  historicalYears: HistoricalYearData[]
): { percentages: number[]; yearsUsed: number[] } {
  // Standard weights: 50% most recent, 30% previous, 20% oldest
  const standardWeights = [0.50, 0.30, 0.20];

  const years: { year: number; pcts: number[]; weight: number }[] = [];

  // Process years in order (most recent first)
  for (let i = 0; i < historicalYears.length && i < 3; i++) {
    const yearData = historicalYears[i];
    if (hasEnoughDataForSeasonality(yearData.monthlyTotals)) {
      years.push({
        year: yearData.year,
        pcts: getMonthlyPercentages(yearData.monthlyTotals),
        weight: standardWeights[i],
      });
    }
  }

  // If no years have enough data, return equal distribution
  if (years.length === 0) {
    return {
      percentages: Array(12).fill(1 / 12),
      yearsUsed: [],
    };
  }

  // Normalize weights to sum to 1.0
  const totalWeight = years.reduce((sum, y) => sum + y.weight, 0);
  const normalizedYears = years.map(y => ({ ...y, weight: y.weight / totalWeight }));

  // Calculate weighted average for each month
  const blended = Array(12).fill(0);
  for (const year of normalizedYears) {
    for (let i = 0; i < 12; i++) {
      blended[i] += year.pcts[i] * year.weight;
    }
  }

  // Normalize to ensure sum is exactly 1.0
  const sum = blended.reduce((a, b) => a + b, 0);
  const percentages = blended.map(v => v / sum);

  return {
    percentages,
    yearsUsed: years.map(y => y.year),
  };
}

/**
 * Apply seasonality percentages to an annual total to get monthly distribution
 * Adjusts for rounding to ensure sum equals annual total exactly
 *
 * @param annualTotal - The annual projection value
 * @param percentages - Array of 12 percentages (0-1)
 * @returns MonthlyTotals with the distribution
 */
export function applySeasonality(annualTotal: number, percentages: number[]): MonthlyTotals {
  const monthly = percentages.map(p => Math.round(annualTotal * p));
  const sum = monthly.reduce((a, b) => a + b, 0);
  const diff = annualTotal - sum;

  // Adjust the month with highest value to fix rounding error
  if (diff !== 0 && monthly.length > 0) {
    const maxIdx = monthly.indexOf(Math.max(...monthly));
    monthly[maxIdx] += diff;
  }

  return {
    month1: monthly[0], month2: monthly[1], month3: monthly[2], month4: monthly[3],
    month5: monthly[4], month6: monthly[5], month7: monthly[6], month8: monthly[7],
    month9: monthly[8], month10: monthly[9], month11: monthly[10], month12: monthly[11],
  };
}

/**
 * Convert percentages array to MonthlyTotals format
 */
export function percentagesToMonthlyTotals(percentages: number[]): MonthlyTotals {
  return {
    month1: percentages[0], month2: percentages[1], month3: percentages[2], month4: percentages[3],
    month5: percentages[4], month6: percentages[5], month7: percentages[6], month8: percentages[7],
    month9: percentages[8], month10: percentages[9], month11: percentages[10], month12: percentages[11],
  };
}

/**
 * Calculate complete seasonality result for an annual projection
 *
 * @param annualTotal - The annual projection value
 * @param historicalYears - Array of historical year data (most recent first)
 * @returns SeasonalityResult with monthly distribution and metadata
 */
export function calculateSeasonalityForProjection(
  annualTotal: number,
  historicalYears: HistoricalYearData[]
): SeasonalityResult {
  const { percentages, yearsUsed } = calculateSeasonalityWeights(historicalYears);
  const monthlyDistribution = applySeasonality(annualTotal, percentages);
  const seasonalityWeights = percentagesToMonthlyTotals(percentages);

  return {
    monthlyDistribution,
    seasonalityWeights,
    yearsUsed,
  };
}
